import { Box, IconButton, TableCell, TableRow } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { JSX } from "react";

import { BulkCheckbox } from "../../../../components/bulk-selection/index.js";
import {
  PlatformPill,
  draftBodyPreview,
  draftDisplayTitle,
  humanAge,
} from "../../shared.js";
import type { Draft } from "../../types.js";

/**
 * One pending-approval queue row. The whole row is the open-review hit target
 * (role=button + keyboard); the selection checkbox and trash button both
 * stop-propagation so they never also open the review modal. Extracted from
 * `PendingApprovalTable.tsx`.
 */
export function DraftRow({
  draft,
  canEdit,
  checked,
  onToggle,
  onOpen,
  onDelete,
}: {
  draft: Draft;
  canEdit: boolean;
  checked: boolean;
  onToggle: (nextChecked: boolean, opts: { shiftKey: boolean }) => void;
  onOpen: () => void;
  onDelete: () => void;
}): JSX.Element {
  return (
    <TableRow
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      data-testid={`pending-row-${draft.id}`}
      title="Open draft to review"
      sx={{
        cursor: "pointer",
        ...(checked
          ? { bgcolor: (t) => t.palette.action.selected }
          : { "&:hover": { bgcolor: "action.hover" } }),
        "&:focus-visible": { bgcolor: "action.hover", outline: "none" },
        "& > td": { borderColor: "divider" },
        "&:last-of-type > td": { borderBottom: 0 },
      }}
    >
      {canEdit && (
        <TableCell sx={{ px: 1, py: 1, verticalAlign: "top" }}>
          <BulkCheckbox
            checked={checked}
            onChange={onToggle}
            ariaLabel={`Select draft ${draft.id}`}
          />
        </TableCell>
      )}
      <TableCell sx={{ px: 1, py: 1, verticalAlign: "top" }}>
        <PlatformPill platform={draft.platform} />
      </TableCell>
      <TableCell sx={{ px: 1, py: 1, verticalAlign: "top" }}>
        <Box
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 500,
            color: "text.primary",
          }}
        >
          {draftDisplayTitle(draft)}
        </Box>
        {draft.title ? (
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            {draftBodyPreview(draft.body).slice(0, 90)}
          </Box>
        ) : null}
      </TableCell>
      <TableCell
        sx={{ px: 1, py: 1, verticalAlign: "top", textAlign: "right", fontFamily: "ui-monospace, monospace", fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}
      >
        {humanAge(draft.created_at)}
      </TableCell>
      {canEdit && (
        <TableCell sx={{ px: 1, py: 1, verticalAlign: "top" }}>
          <IconButton
            size="small"
            aria-label="Delete draft"
            title="Delete draft"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            sx={{
              height: 28,
              width: 28,
              color: "text.secondary",
              "&:hover": {
                bgcolor: (t) => alpha(t.palette.error.main, 0.1),
                color: "error.main",
              },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </TableCell>
      )}
    </TableRow>
  );
}
